/**
 * Live cross-system data for the activity detail page, fetched from the
 * Supergraph in the browser (GRMSB-785 pattern, fail-open):
 *
 *  - CSAT scores (Evaluation service)
 *  - Per-organization registered/attended counts (RMS/AMS)
 *
 * Renders nothing when enrichment hasn't loaded (or failed) — the REST-based
 * page is the baseline and is never blocked by this component.
 */
import { useSelector } from 'react-redux';
import {
    Container,
    Header,
    ColumnLayout,
    Box,
} from '@amzn/awsui-components-react-v3';
import { selectDetailEnrichment } from '../../../../store/slices/selectedActivitySlice';
import { OrgCount } from '../../../../../common/interfaces/supergraph';
import { EMPTY_STRING } from '../../../../../common/constants/grimsby';

const orgCountList = (counts: OrgCount[]) =>
    counts.length === 0
        ? EMPTY_STRING
        : counts.map((entry, index) => (
              <Box key={index} variant="p">
                  {entry.organizationName}: {entry.count}
              </Box>
          ));

const SupergraphEnrichmentSection = () => {
    const enrichment = useSelector(selectDetailEnrichment);

    if (!enrichment) {
        return null;
    }

    const hasAnything =
        enrichment.csat !== null ||
        enrichment.registrationsPerOrg.length > 0 ||
        enrichment.attendedPerOrg.length > 0;

    if (!hasAnything) {
        return null;
    }

    return (
        <Container
            data-testid="SupergraphEnrichmentSection"
            header={<Header variant="h2">Live cross-system data</Header>}
        >
            <ColumnLayout columns={3} variant="text-grid">
                <div>
                    <Box variant="awsui-key-label">CSAT</Box>
                    <div data-testid="EnrichmentCsat">
                        {/* A score of 0.00 with zero responses means "not yet
                            evaluated", not "scored zero" — show that plainly. */}
                        {enrichment.csat === null
                            ? EMPTY_STRING
                            : enrichment.csat.completedResponses === 0
                              ? 'No responses yet'
                              : `${enrichment.csat.overallScore.toFixed(2)} overall · ` +
                                `${enrichment.csat.instructorScore.toFixed(2)} instructor · ` +
                                `${enrichment.csat.completedResponses} responses`}
                    </div>
                </div>
                <div>
                    <Box variant="awsui-key-label">Registered by org</Box>
                    <div data-testid="EnrichmentRegisteredPerOrg">
                        {orgCountList(enrichment.registrationsPerOrg)}
                    </div>
                </div>
                <div>
                    <Box variant="awsui-key-label">Attended by org</Box>
                    <div data-testid="EnrichmentAttendedPerOrg">
                        {orgCountList(enrichment.attendedPerOrg)}
                    </div>
                </div>
            </ColumnLayout>
        </Container>
    );
};

export default SupergraphEnrichmentSection;
